import { RunConfigCase } from '@aero-platform/shared'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { classNames } from 'primereact/utils'
import { FC, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { CompactTableHeaderClassName, NASCAR28Teams } from '../../utils/constants'
import { currentTeamState, currentVehicleState, isF1TeamState } from '../../utils/state-atoms'
import IconButton from '../buttons/IconButton'
import { DeleteIcon, EditIcon, EyeIcon } from '../Icons'
import { safeStringify } from '../../utils/safeJson'
import { getDraftingCaseDisplayName } from './DataTableBodyTemplate'

type NewCaseGroupTableProps = {
  workflowSubmissionId: string
  submissionGroupName: string
  cases: RunConfigCase[]
  isCommitting: boolean
  isDeletingCase: boolean
  isUpdatingCase: boolean
  speedType?: string
  onEditCase: (
    submissionGroupName: string,
    caseName: string,
    caseIndex: number,
    caseKey?: string
  ) => void
  onDeleteCase: (
    submissionGroupName: string,
    caseName: string,
    caseIndex: number,
    caseKey?: string
  ) => void
}

const NewCaseGroupTable: FC<NewCaseGroupTableProps> = ({
  workflowSubmissionId,
  cases,
  submissionGroupName,
  isCommitting,
  isDeletingCase,
  isUpdatingCase,
  speedType,
  onEditCase,
  onDeleteCase
}: NewCaseGroupTableProps) => {
  const [caseToShow, setCaseToShow] = useState<RunConfigCase>()
  const [showCaseDetail, setShowCaseDetail] = useState(false)
  const isF1Team = useRecoilValue(isF1TeamState)
  const currentTeam = useRecoilValue(currentTeamState)
  const currentVehicle = useRecoilValue(currentVehicleState)
  const isNascar28 = NASCAR28Teams.includes(currentTeam)

  const columns = [
    {
      header: 'Name',
      field: 'name',
      headerClassName: CompactTableHeaderClassName,
      bodyClassName: 'font-semibold',
      body: (rowData: RunConfigCase) => {
        return <div>{getDraftingCaseDisplayName(rowData, currentVehicle)}</div>
      }
    },
    {
      header: 'FW',
      bodyClassName: 'w-[75px]',
      field: 'selectedWingState.fwState',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedWingState?.fwState
        return <span>{num && num.toFixed(1)}</span>
      },
      disabled: !isF1Team
    },
    {
      header: 'DRS(%)',
      bodyClassName: 'w-[75px]',
      field: 'selectedWingState.drs',
      disabled: !isF1Team
    },
    {
      header: 'Attitude',
      field: 'selectedAttitudeState',
      bodyClassName: 'w-[120px]',
      body: (rowData: RunConfigCase) => {
        return <div>{rowData.selectedAttitudeState?.name}</div>
      }
    },
    {
      header: 'Domain',
      field: 'selectedDomainState',
      bodyClassName: 'w-[120px]',
      body: (rowData: RunConfigCase) => {
        return <div>{rowData.selectedDomainState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'Tire Set',
      field: 'selectedTyreState',
      bodyClassName: 'w-[100px] min-w-[100px]',
      body: (rowData: RunConfigCase) => {
        return <div>{rowData.selectedTyreState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'Sim Type',
      field: 'selectedSimulationState',
      bodyClassName: 'w-[100px] min-w-[100px]',
      body: (rowData: RunConfigCase) => {
        return <div>{rowData.selectedSimulationState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'FRH',
      field: 'selectedAttitudeState.frh',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.frh
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'RRH',
      field: 'selectedAttitudeState.rrh',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.rrh
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'Steer',
      field: 'selectedAttitudeState.steer',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.steer
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'Roll',
      field: 'selectedAttitudeState.roll',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.roll
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'Yaw',
      field: 'selectedAttitudeState.yaw',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.yaw
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'Rad',
      field: 'selectedAttitudeState.radius',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.radius
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: `Spd${speedType ? ` (${speedType})` : ''}`,
      field: 'selectedAttitudeState.speed',
      bodyClassName: 'w-[90px] max-w-[90px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.speed
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'Exh',
      field: 'selectedAttitudeState.exhaust',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = rowData.selectedAttitudeState?.exhaust
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'SPEC',
      field: 'selectedAttitudeState.spec',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = (rowData.selectedAttitudeState as any)?.spec
        return <span>{num != null ? num.toFixed(1) : ''}</span>
      },
      disabled: !isNascar28
    },
    {
      header: 'RAD_BLOCKER',
      field: 'selectedAttitudeState.radBlocker',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: RunConfigCase) => {
        const num = (rowData.selectedAttitudeState as any)?.radBlocker
        return <span>{num != null ? num.toFixed(1) : ''}</span>
      },
      disabled: !isNascar28
    }
  ]

  return (
    <>
      <DataTable
        value={cases}
        stripedRows
        dataKey="key"
        pt={{
          bodyRow: {
            className: 'table-row'
          }
        }}>
        {false && (
          <Column
            rowEditor
            className="case-checkbox"
            headerClassName="one-col-editor-header editor-header"
            bodyStyle={{ textAlign: 'center', padding: '0px 0px' }}
            body={(rowData, options) => {
              return (
                <div
                  className="flex items-center justify-center min-w-full min-h-full"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}></div>
              )
            }}
          />
        )}

        {columns.map(item => {
          if (item.disabled) {
            return null
          }
          return (
            <Column
              className={classNames({ 'case-name': item.field === 'name' })}
              key={item.field}
              field={item.field}
              header={item.header}
              headerClassName={item.headerClassName}
              bodyClassName={item.bodyClassName}
              body={item.body}></Column>
          )
        })}

        <Column
          rowEditor
          headerClassName="one-col-editor-header editor-header"
          bodyClassName="w-[150px] pr-0"
          bodyStyle={{ textAlign: 'center', paddingBottom: 24 }}
          pt={{
            root: {
              className: 'px-2 py-2 text-center'
            }
          }}
          body={(rowData, options) => {
            const rowName: string = rowData.name
            return (
              <div className="flex items-center justify-end min-w-full min-h-full space-x-1">
                <IconButton
                  severity="info"
                  icon={<EyeIcon className="fill-white dark:fill-light-blue h-4 w-4" />}
                  onClick={() => {
                    setShowCaseDetail(true)
                    setCaseToShow(rowData)
                  }}
                  title="View Case Details"
                />
                <IconButton
                  title="Edit"
                  severity="secondary"
                  onClick={() => {
                    onEditCase(submissionGroupName, rowName, options.rowIndex, rowData.key)
                  }}
                  icon={<EditIcon className="fill-white w-4 h-4" />}
                />
                <IconButton
                  icon={<DeleteIcon className="fill-white m-0.5 h-3 w-3" />}
                  severity="danger"
                  onClick={() => {
                    onDeleteCase(submissionGroupName, rowName, options.rowIndex, rowData.key)
                  }}
                  title="Remove Case"
                />
              </div>
            )
          }}
        />
      </DataTable>
      <Dialog
        header={`${caseToShow && caseToShow.name} Details`}
        visible={showCaseDetail}
        closeOnEscape
        pt={{
          root: {
            className: 'w-[60vw]'
          },
          mask: {
            className: 'bg-blue-backdrop dark:bg-neutral-80'
          },
          content: {
            className: 'max-h-[80vh] overflow-y-auto'
          }
        }}
        onHide={() => {
          if (!showCaseDetail) return
          setShowCaseDetail(false)
        }}>
        <p>
          <pre className="whitespace-pre-wrap">{caseToShow && safeStringify(caseToShow, 4)}</pre>
        </p>
      </Dialog>
    </>
  )
}

export default NewCaseGroupTable
